import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { Check, Mail } from "lucide-react"
import type { DeclarationInquiryState } from "../../../../../api/declarationInquiriesApi"
import { derivationDotVar, dotStyle } from "../derivationSeverity"
import type { FrameworkDerivation } from "../mapping/deriveFrameworkVerdict"

export interface FrameworkRowProps {
  framework: string
  name: string
  derivation: FrameworkDerivation | null
  /** `geprueft(framework)` — a green check when true, a neutral ring otherwise. */
  checked: boolean
  active: boolean
  onSelect: () => void
  /** A sent (never answered — that resolves back to the ordinary dot) Rückfrage
   *  mutes the row and adds a small mail glyph, so the navigator itself says
   *  which Verordnung is waiting on the supplier without opening it. */
  inquiryState?: Exclude<DeclarationInquiryState, "draft">
  /** B1: every card of this framework is `excluded` (rejected) — the
   *  framework is geprüft server-side (its worklist is closed), but the
   *  checked sentence must not read like an ordinary reviewed Verordnung,
   *  or the reviewer reads "nothing left to do here" for the wrong reason.
   *  Only ever consulted while `checked` is true. */
  allExcluded?: boolean
  /** Paged mode (C1-d): the server's open items for this row are being
   *  refetched ("pending") or could not be read ("unknown"). The row then never
   *  shows the green check, whatever `checked` says: a stale or missing answer
   *  is not "geprüft". Null or absent: the ordinary look. */
  regulationStatus?: "pending" | "unknown" | null
}

/**
 * One Verordnung row in the navigator — the prototype's `fwListRow()`
 * (`.fwrow` > `.fwstat` + `.fwnm` + `.fwdot`). The mail glyph has no
 * prototype equivalent (Rückfrage is this app's own concept); it rides in
 * the same trailing slot as the verdict dot rather than adding a fourth grid
 * column the prototype's `.fwrow` grid does not have.
 */
/** The row's status sentence — what the button's title carries alongside the
 *  Verordnung name. `checked` (already reviewed) and a sent Rückfrage both
 *  outrank the raw R1–R4 derivation, since either explains the row's look
 *  on its own; otherwise the sentence collapses the derivation to its
 *  severity family (`verdictMeta`'s ok/warn/fail), the same three-way split
 *  the dot's colour already draws. */
function statusSentence(
  translate: (key: TranslationKeys) => string,
  checked: boolean,
  sent: boolean,
  derivation: FrameworkDerivation | null,
  allExcluded: boolean,
  regulationStatus: "pending" | "unknown" | null,
): string {
  if (regulationStatus === "pending")
    return translate("MC_DECL_V2_NAV_STATE_PENDING")
  if (regulationStatus === "unknown")
    return translate("MC_DECL_V2_NAV_STATE_UNKNOWN")
  if (checked) {
    // The framework counts as geprüft server-side either way — this is a
    // DISTINCT sentence for the same true state, never a claim that more
    // action is needed.
    return translate(
      allExcluded
        ? "MC_DECL_V2_NAV_STATE_CHECKED_ALL_EXCLUDED"
        : "MC_DECL_V2_NAV_STATE_CHECKED",
    )
  }
  if (sent) return translate("MC_DECL_V2_NAV_STATE_INQUIRY_SENT")
  if (!derivation) return translate("MC_DECL_V2_NAV_STATE_WARN")
  switch (derivation.rule) {
    case "R1":
      return translate("MC_DECL_V2_NAV_STATE_WARN")
    case "R2":
      return translate("MC_DECL_V2_NAV_STATE_FAIL")
    case "R3":
      return translate("MC_DECL_V2_NAV_STATE_WARN")
    case "R4":
      return derivation.verdict === "compliant"
        ? translate("MC_DECL_V2_NAV_STATE_OK")
        : translate("MC_DECL_V2_NAV_STATE_WARN")
  }
}

export function FrameworkRow({
  framework,
  name,
  derivation,
  checked,
  active,
  onSelect,
  inquiryState = "none",
  allExcluded = false,
  regulationStatus = null,
}: FrameworkRowProps) {
  const { translate } = useTranslation()
  const dot = dotStyle(derivationDotVar(derivation))
  const sent = inquiryState === "sent"
  const shownChecked = checked && regulationStatus === null
  const status = statusSentence(
    translate,
    shownChecked,
    sent,
    derivation,
    allExcluded,
    regulationStatus,
  )
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onSelect}
            data-testid={`framework-row-${framework}`}
            aria-current={active ? "true" : undefined}
            data-status={regulationStatus ?? undefined}
            className={cn(
              "sv2-fwrow",
              active && "sv2-on",
              shownChecked && "sv2-fwdone",
              regulationStatus && "sv2-fwpending",
            )}
          >
            <span
              aria-hidden="true"
              className={cn("sv2-fwstat", shownChecked && "sv2-fwok")}
            >
              {shownChecked && (
                <Check size={10} strokeWidth={3} className="sv2-ic" />
              )}
            </span>
            <span className={cn("sv2-fwnm", sent && "text-grey5")}>{name}</span>
            <span className="inline-flex shrink-0 items-center gap-1">
              {/* No tooltip of its own — nesting a second Radix Tooltip
                  inside the row's own trigger would fire both on hover
                  (mouseenter is per-target, not just the innermost), showing
                  two overlapping bubbles. The row's own tooltip already
                  names this exact state (`statusSentence`'s `sent` branch),
                  so the icon stays a plain, decorative glyph. */}
              {sent && (
                <Mail
                  data-testid={`framework-row-${framework}-inquiry-sent`}
                  size={11}
                  className="text-grey5"
                  aria-hidden="true"
                />
              )}
              <span aria-hidden="true" className="sv2-fwdot" style={dot} />
            </span>
          </button>
        </TooltipTrigger>
        <TooltipContent side="right">
          {name} · {status}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
